/*
 * @author: zhangning
 * @date: 2022/3/24 17:23
 * @Description: 验证码登录组件
 **/
import React, { ReactElement, useEffect, useState } from 'react';
import { Button, Input, message } from 'antd';
import { encMsg, getPk } from '@/utils/kyberSm2';
import { sendDecryPCode, decryLbCode } from '../../../../api/login/index.js';
import { throttlFun, handleLocHref } from '../../../../utils/index.js';
import { zhugeTrack } from '@/utils/utils';
import { useAppSelector } from '@/redux/hooks';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRequestParams } from '@/utils/index';

const RegPhone = /^[1][3,4,5,6,7,8,9][0-9]{9}$/;

interface paramsType {
  [prop: string]: string;
}

interface Props {
  loginSuccess: (params: any) => void;
  onFirstLogin?: () => void;
}

let intTime = 60;

let clSetTime: ReturnType<typeof setTimeout>;
const CodeLogin: React.FC<Props> = ({ loginSuccess, onFirstLogin }: Props): ReactElement => {
  const translation = useTranslation('login');
  const t = (params: string) => translation.t(params) || '';
  const [messageApi] = message.useMessage();
  // 验证码登录手机号
  const [phone, setPhone] = useState<string>('');
  // 获取验证码文案
  const [codeText, setCodeText] = useState<string>('获取验证码');
  // 验证码输入框内容
  const [code, setCode] = useState<string>('');
  // 验证码登录图形验证码 url
  const [codeImgUrl, setcodeImgUrl] = useState<string>('/uc/code/imageCode.json?username=');
  // 是否显示验证码登录的图形验证码
  const [showCodeImg, setShowCodeImg] = useState<boolean>(false);
  // 验证码登录图形验证码值
  const [codeImg, setCodeImg] = useState<string>('');
  // 获取验证码是否可点击
  const [codeBtnActive, setCodeBtnActive] = useState<boolean>(false);
  // 激活验证码登录按钮
  const [loginActive, setLoginActive] = useState<boolean>(false);
  // 登录loading状态
  const [loginLoading, setLoginLoading] = useState<boolean>(false);
  // 手机号错误提示语
  const [phoneErrText, setPhoneErrText] = useState<string>('');
  // 验证码错误提示语
  const [codeErrText, setCodeErrText] = useState<string>('');
  // 图形验证码错误提示语
  const [codeImgErrText, setCodeImgErrText] = useState<string>('');
  const { language } = useAppSelector((state) => state.common);
  const location = useLocation();
  const navigate = useNavigate();
  // 手机验证码登录
  const handleCodeLogin = async (): Promise<void> => {
    if (!loginActive || loginLoading) {
      return;
    }
    setLoginLoading(true);
    const params: paramsType = {
      tel: phone,
      code: code,
      sign: 'qcloud_pc_tel_login_getcode'
    };
    // 是否需要输入图形验证码
    if (showCodeImg) {
      params.imageCode = codeImg || '';
    }
    // 加密
    const { cipher1, cipher2, salt } = await getPk(true);
    const { cText3: userinfo }: any = await encMsg(JSON.stringify(params), salt);
    params.user = userinfo;
    decryLbCode({ ...params, cipher1, cipher2, salt })
      .then(async (result: { data: any }) => {
        const res = result.data;
        setCodeErrText('');
        setCodeImgErrText('');
        if (res.success) {
          // 判断是否首次登录（验证码登录接口返回 isNewUser 字段）
          if (res.obj?.isNewUser === '1') {
            // 首次登录，调用父组件的首次登录处理
            setLoginLoading(false);
            onFirstLogin?.();
            return;
          } else {
            loginSuccess(res.obj);
          }

          if (!location.search) {
            // window.open(process.env.REACT_APP_API_URL + language, "_self");
            window.open(handleLocHref('') + language, '_self');
          }
          zhugeTrack('用户登录', {
            登录状态: '登录成功',
            登录方式: '验证码登录'
          });
        } else {
          setLoginLoading(false);
          // 10053 和 505 返回的message都是 请输入正确的验证码，并在 验证码输入框下面提示
          // 而且返回 505 的时候要显示 图形验证码
          // 10074 是图形验证码错误
          // 10057 验证码已过期
          zhugeTrack('用户登录', {
            登录状态: '登录失败',
            登录方式: '验证码登录'
          });
          if ([10053, 10057].includes(res.code)) {
            // 提示 验证码错误
            setCodeErrText(res.message);
          } else if (res.code === 505) {
            // 显示图形验证码
            setShowCodeImg(true);
            if (codeImg.length !== 4) {
              setLoginActive(false);
            }
            // 刷新图形验证码
            changeImgCode();
            // 设置验证码错误文案
            setCodeErrText(res.message);
          } else if (res.code === 10074) {
            setShowCodeImg(true);
            changeImgCode();
            // 图形验证码错误文案
            setCodeImgErrText(res.message);
          } else {
            // 别的错误通过这种形式
            messageApi.open({
              type: 'warning',
              content: res.message
            });
          }
        }
      })
      .catch(() => {
        setLoginLoading(false);
      });
  };

  // 获取验证码按钮
  const handleGetCode = async (): Promise<void> => {
    if (!codeBtnActive) {
      return;
    }
    const params: any = {
      type: 'login',
      tel: phone,
      sign: 'qcloud_pc_tel_login_getcode'
    };
    // 发送手机验证码加密
    const { cipher1, cipher2, salt } = await getPk(true);
    const { cText3: userinfo }: any = await encMsg(JSON.stringify(params), salt);
    params.user = userinfo;
    sendDecryPCode({ ...params, cipher1, cipher2, salt }).then((result: { data: any }) => {
      const res = result.data;
      if (res.success) {
        intTime = 60;
        codeCountdown();
        setCodeBtnActive(false);
        message.info('验证码发送成功');
        return;
      } else if (res.code === 0) {
        setPhoneErrText(res.message);
      } else {
        message.error(res.message);
      }
    });
  };

  // 倒计时
  const codeCountdown = (): void => {
    clearTimeout(clSetTime);
    clSetTime = setTimeout(function () {
      intTime--;
      setCodeText(`${intTime}s后重新获取`);
      if (intTime === 0) {
        setCodeText('获取验证码');
        setCodeBtnActive(true);
        clearTimeout(clSetTime);
      } else {
        codeCountdown();
      }
    }, 1000);
  };

  // 手机号改变事件
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setPhone(e.target.value);
    if (RegPhone.test(e.target.value) || !e.target.value) {
      setPhoneErrText('');
    } else {
      setPhoneErrText('手机号格式不正确');
    }
  };

  useEffect(() => {
    if (showCodeImg) {
      setLoginActive(RegPhone.test(phone) && code.length === 6 && codeImg.length === 4);
    } else {
      setLoginActive(RegPhone.test(phone) && code.length === 6);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, phone, codeImg]);

  useEffect(() => {
    return () => {
      clearTimeout(clSetTime);
    };
  }, []);

  // 刷新图形验证码
  const changeImgCode = () => {
    setcodeImgUrl(`/uc/code/imageCode.json?username=${phone}&time=` + new Date().getTime());
  };

  // 更新 获取验证码按钮状态，而且在倒计时的时候不能更新
  useEffect(() => {
    setCodeBtnActive(codeText === '获取验证码' && phoneErrText === '' && phone !== '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneErrText, phone]);

  return (
    <div className="form-box-item">
      <div className="form-box-item-item">
        <Input className={`${phoneErrText ? 'input-error' : ''}`} value={phone} maxLength={11} onChange={handlePhoneChange} placeholder="请输入手机号" />
        <span className="form-item-err-text">{phoneErrText}</span>
      </div>
      <div className="form-box-item-item">
        <Input
          className={`${codeErrText ? 'input-error' : ''}`}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.keyCode === 13 && !showCodeImg) {
              handleCodeLogin();
            }
          }}
          autoComplete="new-password"
          type="text"
          maxLength={6}
          placeholder="请输入验证码"
        />
        <button onClick={() => throttlFun(handleGetCode)} className={`get-code ${codeBtnActive ? 'active' : ''}`}>
          {codeText}
        </button>
        <span className="form-item-err-text">{codeErrText}</span>
      </div>
      {showCodeImg && (
        <div className="form-box-item-item">
          <Input
            className={`${codeImgErrText ? 'input-error' : ''}`}
            value={codeImg}
            onChange={(e) => {
              setCodeImg(e.target.value || '');
            }}
            onKeyDown={(e) => {
              if (e.keyCode === 13) {
                handleCodeLogin();
              }
            }}
            maxLength={4}
            type="text"
          />
          <img className="get-img-code" src={codeImgUrl} onClick={changeImgCode} alt="图形验证码" />
          <span className="form-item-err-text">{codeImgErrText}</span>
        </div>
      )}

      <div className="form-box-item-item">
        <Button onClick={() => throttlFun(handleCodeLogin)} className={`submit ${loginActive && !loginLoading ? 'active' : ''}`} htmlType="submit" disabled={loginLoading}>
          {loginLoading ? (language === 'en' ? 'Logging in...' : '登录中...') : '立即注册/登录'}
        </Button>
      </div>
    </div>
  );
};

export default CodeLogin;
